import React, { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Lightbulb, AlertTriangle, CheckCircle, Users, Globe, Target, MousePointer, Clock, BarChart2 } from 'lucide-react';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

/**
 * InsightCard - Carte d'insight avec tooltip au survol
 */
const InsightCard = ({ insight, style }) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = insight.icon;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: '8px',
        padding: '12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        overflow: 'visible',
        minWidth: 0,
        cursor: 'default',
        position: 'relative'
      }}
    >
      <IconComponent size={18} style={{ color: style.icon, flexShrink: 0, marginTop: '2px' }} />
      <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', marginBottom: '2px', wordBreak: 'break-word' }}>
          {insight.title}
        </div>
        <div style={{ fontSize: '12px', color: '#4b5563', wordBreak: 'break-word' }}>
          {insight.text}
        </div>
      </div>
      {/* Tooltip */}
      {isHovered && insight.details && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px',
          backgroundColor: style.bg,
          border: `1px solid ${style.border}`,
          color: '#374151',
          padding: '10px 12px',
          borderRadius: '8px',
          fontSize: '11px',
          lineHeight: '1.6',
          whiteSpace: 'pre-line',
          zIndex: 9999,
          minWidth: '220px',
          maxWidth: '320px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
        }}>
          {/* Flèche du tooltip */}
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: `8px solid ${style.border}`
          }} />
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '-1px',
            width: 0,
            height: 0,
            borderLeft: '7px solid transparent',
            borderRight: '7px solid transparent',
            borderBottom: `7px solid ${style.bg}`
          }} />
          {insight.details.map((detail, i) => (
            <div key={i} style={{ marginBottom: i < insight.details.length - 1 ? '4px' : 0 }}>
              {detail}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * GA4InsightsPanel - Analyse basée sur les données GA4
 */
const GA4InsightsPanel = ({
  summary,
  previousPeriodData,
  channels = [],
  geography = [],
  events = [],
  formatNumber,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_analysis'
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Calculer les variations par rapport à la période précédente
  const calculateGrowth = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  // Générer les insights sur les performances globales
  const performanceInsights = useMemo(() => {
    if (!summary) return [];

    const insights = [];
    const prev = previousPeriodData || {};

    // Sessions
    const sessionsGrowth = calculateGrowth(summary.totalVisits, prev.totalVisits);
    if (sessionsGrowth !== null) {
      if (sessionsGrowth > 10) {
        insights.push({
          type: 'positive',
          icon: TrendingUp,
          title: 'Trafic en hausse',
          text: `+${formatNumber(sessionsGrowth, 1)}% vs période précédente`,
          details: [
            `Sessions actuelles : ${formatNumber(summary.totalVisits)}`,
            `Sessions précédentes : ${formatNumber(prev.totalVisits)}`,
            `Visiteurs uniques : ${formatNumber(summary.uniqueVisitors || summary.totalVisitors)}`,
            `Pages vues : ${formatNumber(summary.pageviews)}`
          ]
        });
      } else if (sessionsGrowth < -10) {
        insights.push({
          type: 'negative',
          icon: TrendingDown,
          title: 'Baisse du trafic',
          text: `${formatNumber(sessionsGrowth, 1)}% vs période précédente`,
          details: [
            `Sessions actuelles : ${formatNumber(summary.totalVisits)}`,
            `Sessions précédentes : ${formatNumber(prev.totalVisits)}`,
            `Perte de sessions : ${formatNumber(prev.totalVisits - summary.totalVisits)}`,
            `Action : Analyser les sources de trafic`
          ]
        });
      }
    }

    // Taux de conversion
    const convRateGrowth = calculateGrowth(summary.conversionRate, prev.conversionRate);
    if (convRateGrowth !== null) {
      if (convRateGrowth > 15) {
        insights.push({
          type: 'positive',
          icon: Target,
          title: 'Conversions en progression',
          text: `Taux à ${formatNumber(summary.conversionRate, 2)}% (+${formatNumber(convRateGrowth, 1)}%)`,
          details: [
            `Taux actuel : ${formatNumber(summary.conversionRate, 2)}%`,
            `Taux précédent : ${formatNumber(prev.conversionRate, 2)}%`,
            `Conversions : ${formatNumber(summary.conversions)}`,
            `Amélioration : +${formatNumber(convRateGrowth, 1)}%`
          ]
        });
      } else if (convRateGrowth < -15) {
        insights.push({
          type: 'warning',
          icon: AlertTriangle,
          title: 'Taux de conversion en baisse',
          text: `${formatNumber(summary.conversionRate, 2)}% (${formatNumber(convRateGrowth, 1)}%)`,
          details: [
            `Taux actuel : ${formatNumber(summary.conversionRate, 2)}%`,
            `Taux précédent : ${formatNumber(prev.conversionRate, 2)}%`,
            `Baisse : ${formatNumber(Math.abs(convRateGrowth), 1)}%`,
            `Action : Optimiser le parcours de conversion`
          ]
        });
      }
    }

    // Taux de rebond
    if (summary.bounceRate > 70) {
      insights.push({
        type: 'warning',
        icon: AlertTriangle,
        title: 'Taux de rebond élevé',
        text: `${formatNumber(summary.bounceRate, 1)}% des visiteurs quittent immédiatement`,
        details: [
          `Taux de rebond : ${formatNumber(summary.bounceRate, 1)}%`,
          `Seuil recommandé : < 60%`,
          `Sessions concernées : ~${formatNumber(summary.totalVisits * summary.bounceRate / 100)}`,
          `Action : Améliorer les pages d'entrée`
        ]
      });
    } else if (summary.bounceRate < 40) {
      insights.push({
        type: 'positive',
        icon: CheckCircle,
        title: 'Bon engagement',
        text: `Taux de rebond de ${formatNumber(summary.bounceRate, 1)}%`,
        details: [
          `Taux de rebond : ${formatNumber(summary.bounceRate, 1)}%`,
          `Excellent engagement des visiteurs`,
          `Pages/session : ${formatNumber(summary.pagesPerSession || (summary.pageviews / summary.totalVisits), 1)}`,
          `Les visiteurs explorent le site`
        ]
      });
    }

    // Durée moyenne
    if (summary.avgTimeOnSite < 30) {
      insights.push({
        type: 'warning',
        icon: Clock,
        title: 'Sessions courtes',
        text: `Durée moyenne de ${Math.floor(summary.avgTimeOnSite)}s`,
        details: [
          `Durée moyenne : ${Math.floor(summary.avgTimeOnSite)}s`,
          `Durée recommandée : > 60s`,
          `Les visiteurs ne restent pas`,
          `Action : Améliorer le contenu et l'UX`
        ]
      });
    } else if (summary.avgTimeOnSite > 180) {
      insights.push({
        type: 'positive',
        icon: Clock,
        title: 'Sessions engageantes',
        text: `Durée moyenne de ${Math.floor(summary.avgTimeOnSite / 60)}min ${Math.floor(summary.avgTimeOnSite % 60)}s`,
        details: [
          `Durée moyenne : ${Math.floor(summary.avgTimeOnSite / 60)}min ${Math.floor(summary.avgTimeOnSite % 60)}s`,
          `Excellent temps passé sur le site`,
          `Contenu captivant`,
          `Pages/session : ${formatNumber(summary.pagesPerSession || (summary.pageviews / summary.totalVisits), 1)}`
        ]
      });
    }

    return insights;
  }, [summary, previousPeriodData, formatNumber]);

  // Analyser les canaux
  const channelInsights = useMemo(() => {
    if (!channels || channels.length === 0) return [];

    const insights = [];
    const totalSessions = channels.reduce((sum, c) => sum + (c.sessions || 0), 0);
    const totalConversions = channels.reduce((sum, c) => sum + (c.conversions || 0), 0);

    // Meilleur canal par sessions
    const sortedByTraffic = [...channels].sort((a, b) => (b.sessions || 0) - (a.sessions || 0));
    const topChannelSessions = sortedByTraffic[0];
    if (topChannelSessions && totalSessions > 0) {
      const percentage = (topChannelSessions.sessions / totalSessions * 100);
      insights.push({
        type: 'info',
        icon: Users,
        title: `${topChannelSessions.channel || topChannelSessions.name} : canal principal`,
        text: `${formatNumber(percentage, 0)}% du trafic`,
        details: [
          `Canal : ${topChannelSessions.channel || topChannelSessions.name}`,
          `Sessions : ${formatNumber(topChannelSessions.sessions)}`,
          `Part du trafic : ${formatNumber(percentage, 1)}%`,
          `Conversions : ${formatNumber(topChannelSessions.conversions || 0)}`
        ]
      });
    }

    // Meilleur canal par taux de conversion
    const channelsWithConversions = channels
      .filter(c => (c.sessions || 0) > 10 && (c.conversions || 0) > 0)
      .map(c => ({
        ...c,
        convRate: (c.conversions / c.sessions * 100)
      }))
      .sort((a, b) => b.convRate - a.convRate);

    if (channelsWithConversions.length > 0) {
      const topConvertingChannel = channelsWithConversions[0];
      if (topConvertingChannel.convRate > 1) {
        insights.push({
          type: 'positive',
          icon: Target,
          title: `${topConvertingChannel.channel || topConvertingChannel.name} convertit le mieux`,
          text: `${formatNumber(topConvertingChannel.convRate, 2)}% de taux de conversion`,
          details: [
            `Canal : ${topConvertingChannel.channel || topConvertingChannel.name}`,
            `Taux de conversion : ${formatNumber(topConvertingChannel.convRate, 2)}%`,
            `Conversions : ${formatNumber(topConvertingChannel.conversions)}`,
            `Sessions : ${formatNumber(topConvertingChannel.sessions)}`
          ]
        });
      }
    }

    // Canal avec potentiel inexploité (beaucoup de trafic mais peu de conversions)
    const potentialChannels = channels
      .filter(c => (c.sessions || 0) > totalSessions * 0.1)
      .map(c => ({
        ...c,
        convRate: c.sessions > 0 ? (c.conversions / c.sessions * 100) : 0
      }))
      .filter(c => c.convRate < 0.5 && c.sessions > 50);

    if (potentialChannels.length > 0) {
      const lowConvertingChannel = potentialChannels.sort((a, b) => b.sessions - a.sessions)[0];
      insights.push({
        type: 'warning',
        icon: Lightbulb,
        title: `Potentiel inexploité : ${lowConvertingChannel.channel || lowConvertingChannel.name}`,
        text: `${formatNumber(lowConvertingChannel.sessions)} sessions, ${formatNumber(lowConvertingChannel.convRate, 2)}% conv.`,
        details: [
          `Canal : ${lowConvertingChannel.channel || lowConvertingChannel.name}`,
          `Sessions : ${formatNumber(lowConvertingChannel.sessions)}`,
          `Taux de conversion : ${formatNumber(lowConvertingChannel.convRate, 2)}%`,
          `Action : Optimiser ce canal à fort trafic`
        ]
      });
    }

    return insights;
  }, [channels, formatNumber]);

  // Analyser la géographie
  const geoInsights = useMemo(() => {
    if (!geography || geography.length === 0) return [];

    const insights = [];
    const totalSessions = geography.reduce((sum, g) => sum + (g.sessions || 0), 0);

    // Top pays
    const topCountry = geography[0];
    if (topCountry && totalSessions > 0) {
      const percentage = (topCountry.sessions / totalSessions * 100);
      insights.push({
        type: 'info',
        icon: Globe,
        title: `${topCountry.country} : marché principal`,
        text: `${formatNumber(percentage, 0)}% du trafic`,
        details: [
          `Pays : ${topCountry.country}`,
          `Sessions : ${formatNumber(topCountry.sessions)}`,
          `Part du trafic : ${formatNumber(percentage, 1)}%`,
          `Conversions : ${formatNumber(topCountry.conversions || 0)}`
        ]
      });
    }

    // Pays avec meilleur taux de conversion
    const countriesWithConversions = geography
      .filter(g => (g.sessions || 0) > 20 && (g.conversions || 0) > 0)
      .map(g => ({
        ...g,
        convRate: (g.conversions / g.sessions * 100)
      }))
      .sort((a, b) => b.convRate - a.convRate);

    if (countriesWithConversions.length > 0 && countriesWithConversions[0].convRate > 2) {
      const topConvertingCountry = countriesWithConversions[0];
      insights.push({
        type: 'positive',
        icon: Target,
        title: `${topConvertingCountry.country} : meilleur taux de conversion`,
        text: `${formatNumber(topConvertingCountry.convRate, 2)}%`,
        details: [
          `Pays : ${topConvertingCountry.country}`,
          `Taux de conversion : ${formatNumber(topConvertingCountry.convRate, 2)}%`,
          `Conversions : ${formatNumber(topConvertingCountry.conversions)}`,
          `Sessions : ${formatNumber(topConvertingCountry.sessions)}`
        ]
      });
    }

    // Diversification géographique
    if (geography.length >= 5) {
      const top3Sessions = geography.slice(0, 3).reduce((sum, g) => sum + (g.sessions || 0), 0);
      const concentration = totalSessions > 0 ? (top3Sessions / totalSessions * 100) : 0;

      if (concentration < 70) {
        insights.push({
          type: 'positive',
          icon: Globe,
          title: 'Audience diversifiée',
          text: `Trafic sur ${geography.length} pays`,
          details: [
            `Nombre de pays : ${geography.length}`,
            `Concentration top 3 : ${formatNumber(concentration, 0)}%`,
            `Bonne répartition géographique`,
            `Risque réduit de dépendance`
          ]
        });
      }
    }

    return insights;
  }, [geography, formatNumber]);

  // Analyser les événements
  const eventInsights = useMemo(() => {
    if (!events || events.length === 0) return [];

    const insights = [];
    const totalEvents = events.reduce((sum, e) => sum + (e.eventCount || e.count || 0), 0);

    // Top événement
    const sortedEvents = [...events].sort((a, b) => (b.eventCount || b.count || 0) - (a.eventCount || a.count || 0));
    const topEvent = sortedEvents[0];
    if (topEvent && totalEvents > 0) {
      const percentage = ((topEvent.eventCount || topEvent.count) / totalEvents * 100);
      insights.push({
        type: 'info',
        icon: MousePointer,
        title: `${topEvent.eventName || topEvent.name} : événement principal`,
        text: `${formatNumber(percentage, 0)}% des événements`,
        details: [
          `Événement : ${topEvent.eventName || topEvent.name}`,
          `Occurrences : ${formatNumber(topEvent.eventCount || topEvent.count)}`,
          `Part : ${formatNumber(percentage, 1)}%`,
          `Total événements : ${formatNumber(totalEvents)}`
        ]
      });
    }

    // Événements de conversion
    const conversionEvents = events.filter(e =>
      (e.eventName || e.name || '').toLowerCase().includes('purchase') ||
      (e.eventName || e.name || '').toLowerCase().includes('conversion') ||
      (e.eventName || e.name || '').toLowerCase().includes('lead') ||
      (e.eventName || e.name || '').toLowerCase().includes('sign_up') ||
      (e.eventName || e.name || '').toLowerCase().includes('generate_lead')
    );

    if (conversionEvents.length > 0) {
      const totalConvEvents = conversionEvents.reduce((sum, e) => sum + (e.eventCount || e.count || 0), 0);
      if (totalConvEvents > 0) {
        insights.push({
          type: 'positive',
          icon: Target,
          title: 'Événements de conversion',
          text: `${formatNumber(totalConvEvents)} événements`,
          details: [
            `Total conversions : ${formatNumber(totalConvEvents)}`,
            ...conversionEvents.slice(0, 3).map(e =>
              `• ${e.eventName || e.name} : ${formatNumber(e.eventCount || e.count)}`
            )
          ]
        });
      }
    }

    return insights;
  }, [events, formatNumber]);

  // Combiner tous les insights
  const allInsights = [...performanceInsights, ...channelInsights, ...geoInsights, ...eventInsights];

  if (allInsights.length === 0) {
    return null;
  }

  const getInsightStyle = (type) => {
    switch (type) {
      case 'positive':
        return { bg: '#ecfdf5', border: '#10b981', icon: '#059669' };
      case 'negative':
        return { bg: '#fef2f2', border: '#ef4444', icon: '#dc2626' };
      case 'warning':
        return { bg: '#fffbeb', border: '#f59e0b', icon: '#d97706' };
      default:
        return { bg: '#FAF9F5', border: '#1a1a1a', icon: '#333333' };
    }
  };

  return (
    <>
      <div className="section-header">
        <h4 style={{ margin: 0, color: '#1f2937', fontSize: '1.2em', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}><BarChart2 size={20} /> Analyse</h4>
        {toggleClientModuleVisibility ? (
          <ModuleVisibilityToggle
            isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
            onToggle={toggleClientModuleVisibility}
            userRole={userRole}
            moduleId={moduleId}
            title="Analyse GA4"
          />
        ) : (
          <button
            onClick={() => setIsVisible(!isVisible)}
            className="section-toggle-btn"
            title={isVisible ? "Masquer la section" : "Afficher la section"}
          >
            {isVisible ? '−' : '+'}
          </button>
        )}
      </div>

      {isVisible && (
        <div>
          {allInsights.length > 0 && (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '12px',
                maxWidth: '100%'
              }}>
                {allInsights.slice(0, 10).map((insight, index) => (
                  <InsightCard
                    key={index}
                    insight={insight}
                    style={getInsightStyle(insight.type)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default GA4InsightsPanel;
